import { ReactNode, useCallback, useEffect } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Heading from "@atlaskit/heading";
import { RovoLogo } from "@atlaskit/logo";
import { Inline, Stack, xcss } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { SignupBackgroundLayout } from "@/components/signup/layouts";

import { useAnalyticsClient } from "../../../../../../../analytics/wac-analytics/analytics-client";
import { useLocalizedHref } from "../../../../../../../libraries/web-framework/src/localization/use-localized-href";
import { messages } from "../messages";

import { ErrorIconImage } from "./error-icon-svg";

const elevatedContainerInnerStyles = xcss({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "space.400",
    alignSelf: "stretch",
    paddingBlockStart: "space.150"
});

const contentStyles = xcss({
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "space.200"
});

const bodyStyles = xcss({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    alignSelf: "stretch",
    fontSize: token("font.heading.small"),
    fontWeight: "400"
});

const linkStyles = css({
    textAlign: "center",
    color: token("color.link"),
    fontWeight: "700"
});

export const ErrorScreen = () => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();
    const localizedContactHref = useLocalizedHref("/enterprise/contact");

    useEffect(() => {
        analyticsClient &&
            createAnalyticsEvent({
                action: "viewed",
                eventType: "screen",
                name: "softwareRovoStartPageErrorScreen",
                source: "softwareRovoStartPage"
            }).fire();
    }, [analyticsClient, createAnalyticsEvent]);

    const fireButtonAnalytics = useCallback(
        (buttonName: string) => {
            analyticsClient &&
                createAnalyticsEvent({
                    action: "clicked",
                    actionSubject: buttonName,
                    actionSubjectId: "softwareRovoStartPageTemplate",
                    attributes: {
                        source: "errorScreen"
                    }
                }).fire();
        },
        [analyticsClient, createAnalyticsEvent]
    );

    const contactSupportLinkHandler = useCallback(
        () => fireButtonAnalytics("contactSupportLink"),
        [fireButtonAnalytics]
    );

    const contactSalesLinkHandler = useCallback(
        () => fireButtonAnalytics("contactSalesLink"),
        [fireButtonAnalytics]
    );

    return (
        <SignupBackgroundLayout
            ProductLogo={<RovoLogo size="xlarge" appearance="brand" />}
        >
            <Stack xcss={elevatedContainerInnerStyles}>
                <ErrorIconImage />
                <Stack xcss={contentStyles}>
                    <Heading as="h1">
                        <FormattedMessage {...messages.errorScreenTitle} />
                    </Heading>
                    <Stack xcss={bodyStyles}>
                        <Inline space="space.075">
                            <FormattedMessage
                                {...messages.errorScreenMessageOne}
                                values={{
                                    a: (text: ReactNode) => (
                                        <a
                                            css={linkStyles}
                                            href="https://support.atlassian.com/contact/"
                                            onClick={contactSupportLinkHandler}
                                            aria-label="Contact Atlassian Support Page"
                                        >
                                            {text}
                                        </a>
                                    )
                                }}
                            />
                        </Inline>
                        <Inline space="space.075">
                            <FormattedMessage
                                {...messages.errorScreenMessageTwo}
                                values={{
                                    a: (text: ReactNode) => (
                                        <a
                                            css={linkStyles}
                                            href={localizedContactHref}
                                            onClick={contactSalesLinkHandler}
                                            aria-label="Contact Atlassian Sales Page"
                                        >
                                            {text}
                                        </a>
                                    )
                                }}
                            />
                        </Inline>
                    </Stack>
                </Stack>
            </Stack>
        </SignupBackgroundLayout>
    );
};
